import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useBrandsStore } from "../../../../../../store/reseller-admin/brandsStore";
import { useCompaniesStore } from "../../../../../../store/admin/companiesStore";
import { useBrandsList } from "../../../../../../features/reseller/queries";
import type { Brand, BrandFormData, AlertState, FilterParams } from "../types";
import { SEARCH_DEBOUNCE_MS } from "../../../../../../hooks/useDebounce";
import Swal from "sweetalert2";

const initialBrandForm: BrandFormData = {
  brand: "",
  model: "",
  network: {
    ip_address: "",
    port: "",
    protocol: "TCP",
  },
  operational_alias: "",
  origin: null,
};

export function useModelForm() {
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCommandsModalOpen, setIsCommandsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"details" | "edit">("details");
  const [editForm, setEditForm] = useState<Partial<BrandFormData>>({});
  const [newBrandForm, setNewBrandForm] = useState<BrandFormData>(initialBrandForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    title: "",
    message: "",
    type: "info",
  });

  // Filter states
  const [searchInput, setSearchInput] = useState(""); // Immediate input value
  const [searchQuery, setSearchQuery] = useState(""); // Debounced value for API
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const itemsPerPage = 6;
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Debounce search input
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchInput]);

  const {
    createBrand,
    updateBrand,
    deleteBrand,
    setSelectedBrand: storeSetSelectedBrand,
  } = useBrandsStore();

  // Compañía seleccionada en el header (reseller). Al cambiarla, recargamos
  // los modelos para mostrar solo los de los dispositivos de esa compañía.
  const companyId = useCompaniesStore((state) => state.companyId);

  const filterParams = useMemo(
    (): FilterParams => ({
      page: currentPage,
      limit: itemsPerPage,
      search: searchQuery || undefined,
    }),
    [currentPage, itemsPerPage, searchQuery],
  );

  const brandsQuery = useBrandsList(filterParams);
  const brands = brandsQuery.data?.data ?? [];
  const brandsMeta = brandsQuery.data?.meta ?? null;
  const brandsLoading = brandsQuery.isLoading;

  const queryClient = useQueryClient();
  const invalidateBrands = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["resellerModels"] });
    queryClient.invalidateQueries({ queryKey: ["resellerCounts"] });
  }, [queryClient]);

  // Volver a página 1 cuando cambia la compañía
  useEffect(() => {
    setCurrentPage(1);
  }, [companyId]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    // Note: currentPage reset is handled in debounce effect
  };

  const handleEditClick = (brand: Brand) => {
    setSelectedBrand(brand);
    storeSetSelectedBrand(brand);
    setEditForm({
      brand: brand.brand || "",
      model: brand.model || "",
      network: brand.network || { ip_address: "", port: "", protocol: "TCP" },
      operational_alias: brand.operational_alias || "",
      origin: brand.origin || null,
    });
    setModalType("edit");
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleDetailsClick = (brand: Brand) => {
    setSelectedBrand(brand);
    storeSetSelectedBrand(brand);
    setModalType("details");
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleCommandsClick = (brand: Brand) => {
    setSelectedBrand(brand);
    storeSetSelectedBrand(brand);
    setIsCommandsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleInputChange = (field: string, value: any) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNewBrandInputChange = (field: string, value: any) => {
    setNewBrandForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveChanges = async (formData: Partial<BrandFormData>) => {
    if (!selectedBrand) return;

    setIsSubmitting(true);

    const result = await updateBrand(selectedBrand.id, formData);

    setIsSubmitting(false);

    if (result.success) {
      setIsModalOpen(false);
      setSelectedBrand(null);
      setEditForm({});
      invalidateBrands();
      setAlert({
        show: true,
        title: "Éxito",
        message: "Modelo actualizado exitosamente",
        type: "success",
      });
    } else {
      setAlert({
        show: true,
        title: "Error",
        message: result.error || "Error al actualizar el modelo",
        type: "error",
      });
    }
  };

  const handleAddBrand = async (formData: BrandFormData) => {
    setIsSubmitting(true);

    const result = await createBrand(formData);

    setIsSubmitting(false);

    if (result.success) {
      setAlert({
        show: true,
        title: "Éxito",
        message: "Modelo creado exitosamente",
        type: "success",
      });
      setIsAddModalOpen(false);
      setNewBrandForm(initialBrandForm);
      invalidateBrands();
    } else {
      setAlert({
        show: true,
        title: "Error",
        message: result.error || "Error al crear el modelo",
        type: "error",
      });
    }
  };

  const handleDeleteBrand = async (id: string) => {
    const target = brands.find((b) => b.id === id);
    const label = target
      ? `${target.brand ?? ""} ${target.model ?? ""}`.trim()
      : "";

    const confirm = await Swal.fire({
      title: "¿Eliminar modelo?",
      text: label
        ? `¿Realmente deseas eliminar el modelo "${label}"?`
        : "¿Realmente deseas eliminar este modelo?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;

    const result = await deleteBrand(id);

    if (result.success) {
      setAlert({
        show: true,
        title: "Éxito",
        message: "Modelo eliminado exitosamente",
        type: "success",
      });
      invalidateBrands();
    } else {
      setAlert({
        show: true,
        title: "Error",
        message: result.error || "Error al eliminar el modelo",
        type: "error",
      });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedBrand(null);
    document.body.style.overflow = "";
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    document.body.style.overflow = "";
  };

  const closeCommandsModal = () => {
    setIsCommandsModalOpen(false);
    document.body.style.overflow = "";
  };

  const openAddModal = () => {
    setIsAddModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  return {
    // Data
    brands,
    meta: brandsMeta,
    loading: brandsLoading,
    selectedBrand,
    editForm,
    newBrandForm,
    alert,
    isSubmitting,

    // Modal states
    isModalOpen,
    isAddModalOpen,
    isCommandsModalOpen,
    modalType,

    // Filter states
    searchQuery: searchInput, // Return input value for UI display
    currentPage,
    selectedView,
    itemsPerPage,

    // Handlers
    handleSearchChange,
    handleEditClick,
    handleDetailsClick,
    handleCommandsClick,
    handleInputChange,
    handleNewBrandInputChange,
    handleSaveChanges,
    handleAddBrand,
    handleDeleteBrand,
    setCurrentPage,
    setSelectedView,
    closeModal,
    closeAddModal,
    closeCommandsModal,
    openAddModal,
    closeAlert,
  };
}
